import {
    computed,
    defineComponent,
    getCurrentInstance,
    h,
    nextTick,
    onScopeDispose,
    reactive,
    ref,
    render,
    watchEffect,
    type AppContext,
    type Component,
    type ComponentInternalInstance,
    type ComponentOptionsBase,
    type MaybeRef,
    type VNode,
    type WatchStopHandle,
} from 'vue';
import type {CamelCase} from 'type-fest';
import type {ComponentProps} from 'vue-component-type-helpers';
import {useNuxtApp} from '#app';
import {VJoyBottomSheet, VJoyDrawer, VJoyDialog} from '@maltjoy/core-vue';

export type CommonEventsProps = {
    'overlay-confirm'?: (...args: unknown[]) => void | Promise<void>;
    'overlay-hide'?: (...args: unknown[]) => void | Promise<void>;
    'overlay-cancel'?: (...args: unknown[]) => void | Promise<void>;
};

type ToEmitHandlers<E> =
    E extends Record<string, any>
        ? {
              [K in keyof E as CamelCase<`on-${string & K}`>]?: E[K];
          }
        : Record<string, any>;

export type UseOverlayOptions<
    T extends Component,
    P = ComponentProps<T>,
    E = T extends ComponentOptionsBase<any, any, any, any, any, any, any, infer E, any, any> ? E : never,
> = {
    props?: {[K in keyof P]: MaybeRef<P[K]>};
    onHide?: () => void | Promise<void>;
    onCancel?: () => void | Promise<void>;
    onConfirm?: 'onConfirm' extends keyof P ? P['onConfirm'] : () => void | Promise<void>;
    emits?: ToEmitHandlers<Exclude<E, string[]>>;
    immediate?: boolean;
};

type JoyOverlayInstance = {
    hide?: (options?: {fireHideEvent?: boolean}) => void;
};

function hideJoyOverlayIfPossible(instance: ComponentInternalInstance | null | undefined): boolean {
    if (!instance) {
        return false;
    }

    const exposed = instance.exposed as JoyOverlayInstance | null | undefined;
    if (typeof exposed?.hide === 'function') {
        exposed.hide({fireHideEvent: false});
        return true;
    }

    for (const ref of Object.values(instance.refs)) {
        const joyRef = ref as JoyOverlayInstance | null | undefined;
        if (typeof joyRef?.hide === 'function') {
            joyRef.hide({fireHideEvent: false});
            return true;
        }
    }

    return hideJoyOverlayIfPossible(instance.subTree?.component ?? null);
}

export function useCommonRenderLogic<T extends Component>(OverlayComponent: T, options?: UseOverlayOptions<T>) {
    let hostBody: HTMLDivElement | undefined;
    let overlayNode: VNode;
    let stopWatching: WatchStopHandle | undefined;
    let teardownScheduled = false;

    const reactiveProps = reactive(options?.props ?? {});
    const isDisplayed = ref(false);

    const nuxtApp = useNuxtApp();

    function teardownOverlay() {
        if (!hostBody || teardownScheduled) {
            return;
        }

        teardownScheduled = true;
        stopWatching?.();
        stopWatching = undefined;
        isDisplayed.value = false;

        const body = hostBody;
        hostBody = undefined;

        // Defer DOM teardown so Vue can finish patching overlay nodes first.
        nextTick(() => {
            requestAnimationFrame(() => {
                render(null, body);
                body.parentNode?.removeChild(body);
                teardownScheduled = false;
            });
        });
    }

    async function onConfirm(...args: any[]) {
        try {
            return await options?.onConfirm?.(...args);
        } finally {
            teardownOverlay();
        }
    }
    function onHide() {
        options?.onHide?.();
        teardownOverlay();
    }
    async function onCancel() {
        try {
            return await options?.onCancel?.();
        } finally {
            teardownOverlay();
        }
    }

    function openOverlay<P extends ComponentProps<T>>(props?: {[K in keyof P]: MaybeRef<P[K]>}) {
        if (import.meta.client && !isDisplayed.value) {
            hostBody = document.createElement('div');

            stopWatching = watchEffect(() => {
                if (!hostBody) {
                    return;
                }

                // Handle reactive props changes
                overlayNode = h(OverlayComponent, {
                    ...reactiveProps,
                    ...props,
                    overlayConfirm: onConfirm,
                    overlayCancel: onCancel,
                    'onOverlay:confirm': onConfirm,
                    'onOverlay:hide': onHide,
                    'onOverlay:cancel': onCancel,
                    ...options?.emits,
                    onConfirm,
                    onHide,
                    onCancel,
                });
                overlayNode.appContext = nuxtApp.vueApp._context as unknown as AppContext;

                render(overlayNode, hostBody);
                if (!hostBody.parentNode) {
                    document.body.appendChild(hostBody);
                }
                isDisplayed.value = true;
            });
        }
    }

    function closeOverlay() {
        hideJoyOverlayIfPossible(overlayNode?.component ?? null);
        teardownOverlay();
    }

    onScopeDispose(closeOverlay);

    if (options?.immediate) {
        openOverlay();
    }

    return {
        openOverlay,
        closeOverlay,
    };
}

type OverlayType = 'Dialog' | 'BottomSheet' | 'Drawer';

type OverlayComponentMapper = {
    Dialog: typeof VJoyDialog;
    BottomSheet: typeof VJoyBottomSheet;
    Drawer: typeof VJoyDrawer;
};

/**
 * To be used on *the component containing the Dialog content*
 */
export function useCommonOverlayRootLogic<T extends OverlayType>(type: T, open = true) {
    const vm = getCurrentInstance();
    const emit = vm?.emit || vm?.emit?.bind(vm) || vm?.proxy?.$emit?.bind(vm?.proxy);

    function getProps() {
        const props = (vm?.props || vm?.proxy?.$props) as {
            overlayConfirm?: (...args: any[]) => void | Promise<void>;
            overlayCancel?: (...args: any[]) => void | Promise<void>;
        };
        return props;
    }

    const loadingConfirm = ref(false);
    const loadingCancel = ref(false);

    const vJoyOverlayRef = ref<InstanceType<OverlayComponentMapper[T]>>();

    async function onConfirm(...args: any[]) {
        const props = getProps();
        try {
            if (props.overlayConfirm) {
                loadingConfirm.value = true;
                await props.overlayConfirm?.(...args);
            } else {
                emit?.('overlay:confirm', ...args);
            }
        } finally {
            loadingConfirm.value = false;
        }
    }
    function onHide() {
        emit?.('overlay:hide');
    }
    function onAfterHide() {
        emit?.('overlay:hide');
    }
    async function onCancel() {
        const props = getProps();
        try {
            if (props.overlayCancel) {
                loadingCancel.value = true;
                await props.overlayCancel?.();
            } else {
                emit?.('overlay:cancel');
            }
        } finally {
            loadingCancel.value = false;
        }
    }

    function defineOverlayComponent() {
        let component: OverlayComponentMapper[keyof OverlayComponentMapper];
        let events: Record<string, any> = {};
        if (type === 'BottomSheet') {
            component = VJoyBottomSheet;
            events = {'onBottomSheet:hide': onHide};
        } else if (type === 'Dialog') {
            component = VJoyDialog;
            events = {
                'onDialog:confirm': onConfirm,
                'onDialog:cancel': onCancel,
                'onDialog:after-hide': onAfterHide,
            };
        } else {
            component = VJoyDrawer;
            events = {'onDrawer:after-hide': onAfterHide};
        }

        return defineComponent({
            setup(props, {slots}) {
                const patternConfirm = computed(() => {
                    if (loadingConfirm.value || loadingCancel.value) {
                        return true;
                    }
                    return false;
                });
                const overlayProps: Record<string, unknown> = {
                    ref: vJoyOverlayRef,
                    open,
                    ...props,
                    ...events,
                    patternConfirm: patternConfirm.value,
                };

                if (type !== 'BottomSheet') {
                    overlayProps.disableTeleport = true;
                }

                return () => h(component, overlayProps, slots);
            },
        });
    }

    const PreloadedOverlayComponent = defineOverlayComponent();

    function openOverlay() {
        vJoyOverlayRef.value?.show();
    }

    async function confirmOverlay(...args: any[]) {
        try {
            await onConfirm(...args);
            vJoyOverlayRef.value?.hide({fireHideEvent: false});
        } catch (e) {
            console.error(e);
        }
    }

    function closeOverlay() {
        vJoyOverlayRef.value?.hide({fireHideEvent: true});
    }

    async function cancelOverlay() {
        await onCancel();
        vJoyOverlayRef.value?.hide({fireHideEvent: false});
    }

    return {PreloadedOverlayComponent, openOverlay, closeOverlay, confirmOverlay, cancelOverlay, loadingCancel, loadingConfirm};
}
